//Ref: https://github.com/open-telemetry/opentelemetry-js-contrib/blob/main/plugins/web/opentelemetry-instrumentation-document-load/src/instrumentation.ts

import { context, Span, SpanStatusCode, trace, Tracer } from "@opentelemetry/api";
import { otperformance } from "@opentelemetry/core";
import { hasKey, PerformanceEntries } from "@opentelemetry/sdk-trace-web";

import CONV from "../../semantic-conventions";
import { RootSpan } from "../../types";
import { getLastPathWithQuery } from "../../utils";
import { log } from "../../utils/logger";
import { SEMANTIC_CONVENTIONS_MAP } from "./constants";
import { DocumentLoadInstrumentationConfig, PerformanceTimingNames as PTN } from "./types";
import {
  addSpanNetworkEvent,
  addSpanNetworkEvents,
  addSpanPerformancePaintEvents,
  getPerformanceNavigationEntries,
} from "./utils";

export class DocumentLoadInstrumentation {
  private tracer: Tracer;
  private config: DocumentLoadInstrumentationConfig | undefined;
  private currentRootSpan: RootSpan = undefined;

  constructor(tracer: Tracer, config?: DocumentLoadInstrumentationConfig) {
    this.tracer = tracer;
    this.config = config;
  }

  public subscribe(rootSpan: RootSpan) {
    this.currentRootSpan = rootSpan;
    log("[L9RUM DocumentLoad] Subscribing to Document Load Instrumentation");
    // remove previously attached load to avoid adding the same event twice
    // in case of multiple enable calling.
    window.removeEventListener("load", this._onDocumentLoaded);
    this._waitForPageLoad();
  }

  public unsubscribe() {
    this.currentRootSpan = undefined;
    window.removeEventListener("load", this._onDocumentLoaded);
  }

  /**
   * callback to be executed when page is loaded
   */
  private _onDocumentLoaded() {
    // Timeout is needed as load event doesn't have yet the performance metrics for loadEnd.
    // Support for event "loadend" is very limited and cannot be used
    window.setTimeout(() => {
      this._collectPerformance();
    });
  }

  /**
   * Adds spans for all resources
   */
  private _addResourcesSpans(): void {
    const resources: PerformanceResourceTiming[] = (
      otperformance as unknown as Performance
    ).getEntriesByType?.("resource") as PerformanceResourceTiming[];

    if (resources) {
      resources.forEach((resource) => {
        this._initResourceSpan(resource);
      });
    }
  }

  /**
   * Collects information about performance and creates appropriate spans
   */
  private _collectPerformance() {
    if (!this.currentRootSpan) {
      return;
    }
    const entries = getPerformanceNavigationEntries();

    context.with(trace.setSpan(context.active(), this.currentRootSpan), () => {
      const rootSpan = this._startSpan(
        location.href, // document load span
        PTN.FETCH_START,
        entries,
      );
      if (!rootSpan) {
        return;
      }

      rootSpan.setAttribute(CONV.HTTP_URL, location.href);
      rootSpan.setAttribute(CONV.HTTP_METHOD, "GET");
      rootSpan.setAttribute(CONV.HTTP_USER_AGENT, navigator.userAgent);
      context.with(trace.setSpan(context.active(), rootSpan), () => {
        if (!this.config?.ignoreNetworkEvents) {
          addSpanNetworkEvents(rootSpan, entries);
        }
      });

      this._addResourcesSpans();

      if (!this.config?.ignoreNetworkEvents) {
        addSpanNetworkEvent(rootSpan, PTN.FETCH_START, entries);
        addSpanNetworkEvent(rootSpan, PTN.UNLOAD_EVENT_START, entries);
        addSpanNetworkEvent(rootSpan, PTN.UNLOAD_EVENT_END, entries);
        addSpanNetworkEvent(rootSpan, PTN.DOM_INTERACTIVE, entries);
        addSpanNetworkEvent(rootSpan, PTN.DOM_CONTENT_LOADED_EVENT_START, entries);
        addSpanNetworkEvent(rootSpan, PTN.DOM_CONTENT_LOADED_EVENT_END, entries);
        addSpanNetworkEvent(rootSpan, PTN.DOM_COMPLETE, entries);
        addSpanNetworkEvent(rootSpan, PTN.LOAD_EVENT_START, entries);
        addSpanNetworkEvent(rootSpan, PTN.LOAD_EVENT_END, entries);
      }

      if (!this.config?.ignorePerformancePaintEvents) {
        addSpanPerformancePaintEvents(rootSpan);
      }

      this._endSpan(rootSpan, PTN.LOAD_EVENT_END, entries);
    });
  }

  /**
   * Helper function for ending span
   * @param span
   * @param performanceName name of performance entry for time end
   * @param entries
   */
  private _endSpan(span: Span | undefined, performanceName: string, entries: PerformanceEntries) {
    // span can be undefined when entries are missing the certain performance - the span will not be created
    if (span) {
      if (hasKey(entries, performanceName)) {
        span.end(entries[performanceName]);
      } else {
        span.end();
      }
    }
  }

  /**
   * Creates and ends a span with network information about resource added as timed events
   * @param resource
   */
  private _initResourceSpan(resource: PerformanceResourceTiming) {
    const span = this._startSpan(
      getLastPathWithQuery(resource.name), // resource fetch span
      PTN.FETCH_START,
      resource,
    );
    if (span) {
      // Default to OK status
      if (typeof span.setStatus === "function") {
        span.setStatus({ code: SpanStatusCode.OK });
      }

      span.setAttribute(CONV.HTTP_URL, resource.name);
      span.setAttribute(CONV.INITIATOR_TYPE, resource.initiatorType);
      if (!this.config?.ignoreNetworkEvents) {
        addSpanNetworkEvents(span, resource);
      }
      this._endSpan(span, PTN.RESPONSE_END, resource);
    }
  }

  /**
   * Helper function for starting a span
   * @param spanName name of span
   * @param performanceName name of performance entry for time start
   * @param entries
   */
  private _startSpan(
    spanName: string,
    performanceName: string,
    entries: PerformanceEntries,
  ): Span | undefined {
    if (hasKey(entries, performanceName) && typeof entries[performanceName] === "number") {
      const parentContext = this.currentRootSpan
        ? trace.setSpan(context.active(), this.currentRootSpan)
        : undefined;
      const span = this.tracer.startSpan(
        spanName,
        {
          startTime: entries[performanceName],
        },
        parentContext,
      );
      if (span) {
        ["responseStatus", "deliveryType"].forEach((key) => {
          const value = (entries as any)[key];
          if (value) {
            const semanticConventionKey =
              SEMANTIC_CONVENTIONS_MAP[key as keyof typeof SEMANTIC_CONVENTIONS_MAP] || key;
            span.setAttribute(semanticConventionKey, value);
          }
        });

        log(`[L9RUM DocumentLoad] ${spanName} started`, JSON.stringify(entries));
      }
      return span;
    }
    return undefined;
  }

  /**
   * executes callback {_onDocumentLoaded} when the page is loaded
   */
  private _waitForPageLoad() {
    if (window.document.readyState === "complete") {
      this._onDocumentLoaded();
    } else {
      this._onDocumentLoaded = this._onDocumentLoaded.bind(this);
      window.addEventListener("load", this._onDocumentLoaded);
    }
  }
}
